import type { Lang, Level, Rank, Role } from '@dc1/shared';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';

// Query keys in one place so invalidation never misses a cache entry.
export const qk = {
  me: ['me'] as const,
  results: ['results', 'me'] as const,
  history: ['results', 'me', 'history'] as const,
  whatsNew: ['results', 'me', 'whats-new'] as const,
  members: ['members'] as const,
  member: (id: string) => ['members', id] as const,
  pending: (id: string) => ['members', id, 'pending'] as const,
  dashboard: ['dashboard'] as const,
  progress: ['dashboard', 'progress'] as const,
  periods: ['periods'] as const,
  team: ['team'] as const,
  notices: ['me', 'notices'] as const,
  teamSettings: ['team-settings'] as const,
  journal: ['journal', 'me'] as const,
  memberJournal: (id: string) => ['members', id, 'journal'] as const,
  /** Under the member's key: confirming or undoing (which invalidate qk.member) refreshes it. */
  memberConfirmations: (id: string) => ['members', id, 'confirmations'] as const,
  teamCoverage: ['team', 'coverage'] as const,
  teamExperts: ['team', 'experts'] as const,
  devUsers: ['dev-users'] as const,
};

const fetchMe = () => unwrap(api.GET('/api/v1/me'));
const fetchResults = () => unwrap(api.GET('/api/v1/results/me'));
const fetchHistory = () => unwrap(api.GET('/api/v1/results/me/history'));
const fetchWhatsNew = () => unwrap(api.GET('/api/v1/results/me/whats-new'));
const fetchMembers = () => unwrap(api.GET('/api/v1/members'));
const fetchMember = (userId: string) =>
  unwrap(api.GET('/api/v1/members/{userId}', { params: { path: { userId } } }));
const fetchPending = (userId: string) =>
  unwrap(api.GET('/api/v1/members/{userId}/pending', { params: { path: { userId } } }));
const fetchDashboard = () => unwrap(api.GET('/api/v1/dashboard'));
const fetchProgress = () => unwrap(api.GET('/api/v1/dashboard/progress'));
const fetchPeriods = () => unwrap(api.GET('/api/v1/periods'));
const fetchJournal = () => unwrap(api.GET('/api/v1/journal/me'));
const fetchMemberJournal = (userId: string) =>
  unwrap(api.GET('/api/v1/members/{userId}/journal', { params: { path: { userId } } }));
const fetchNotices = () => unwrap(api.GET('/api/v1/me/notices'));
const fetchTeamSettings = () => unwrap(api.GET('/api/v1/team/settings'));
const fetchMemberConfirmations = (userId: string) =>
  unwrap(api.GET('/api/v1/members/{userId}/confirmations', { params: { path: { userId } } }));
const fetchTeamCoverage = () => unwrap(api.GET('/api/v1/team/coverage'));
const fetchTeamExperts = () => unwrap(api.GET('/api/v1/team/experts'));
const fetchDevUsers = () => unwrap(api.GET('/api/v1/auth/dev-users'));

export type Me = Awaited<ReturnType<typeof fetchMe>>;
export type Profile = Awaited<ReturnType<typeof fetchResults>>;
export type History = Awaited<ReturnType<typeof fetchHistory>>;
export type WhatsNew = Awaited<ReturnType<typeof fetchWhatsNew>>;
export type Members = Awaited<ReturnType<typeof fetchMembers>>;
export type Pending = Awaited<ReturnType<typeof fetchPending>>;
export type Dashboard = Awaited<ReturnType<typeof fetchDashboard>>;
export type Progress = Awaited<ReturnType<typeof fetchProgress>>;
export type Periods = Awaited<ReturnType<typeof fetchPeriods>>;
export type Journal = Awaited<ReturnType<typeof fetchJournal>>;
export type JournalEntry = Journal['entries'][number];
export type Notices = Awaited<ReturnType<typeof fetchNotices>>;
export type TeamSettings = Awaited<ReturnType<typeof fetchTeamSettings>>;
export type TeamCoverage = Awaited<ReturnType<typeof fetchTeamCoverage>>;
export type TeamExperts = Awaited<ReturnType<typeof fetchTeamExperts>>;

export const meQuery = queryOptions({ queryKey: qk.me, queryFn: fetchMe, staleTime: 60_000, retry: false });
export const resultsQuery = queryOptions({ queryKey: qk.results, queryFn: fetchResults });
export const historyQuery = queryOptions({ queryKey: qk.history, queryFn: fetchHistory });
export const whatsNewQuery = queryOptions({ queryKey: qk.whatsNew, queryFn: fetchWhatsNew });
export const membersQuery = queryOptions({ queryKey: qk.members, queryFn: fetchMembers });
export const memberQuery = (id: string) =>
  queryOptions({ queryKey: qk.member(id), queryFn: () => fetchMember(id) });
export const pendingQuery = (id: string) =>
  queryOptions({ queryKey: qk.pending(id), queryFn: () => fetchPending(id) });
export const dashboardQuery = queryOptions({ queryKey: qk.dashboard, queryFn: fetchDashboard });
export const progressQuery = queryOptions({ queryKey: qk.progress, queryFn: fetchProgress });
export const periodsQuery = queryOptions({ queryKey: qk.periods, queryFn: fetchPeriods });
export const journalQuery = queryOptions({ queryKey: qk.journal, queryFn: fetchJournal });
export const memberJournalQuery = (id: string) =>
  queryOptions({ queryKey: qk.memberJournal(id), queryFn: () => fetchMemberJournal(id) });
/** The bell polls while the app is open, and on focus: a confirmation shows up within a minute. */
export const noticesQuery = queryOptions({
  queryKey: qk.notices,
  queryFn: fetchNotices,
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
});
export const teamSettingsQuery = queryOptions({ queryKey: qk.teamSettings, queryFn: fetchTeamSettings });
export const memberConfirmationsQuery = (id: string) =>
  queryOptions({ queryKey: qk.memberConfirmations(id), queryFn: () => fetchMemberConfirmations(id) });
export const teamCoverageQuery = queryOptions({ queryKey: qk.teamCoverage, queryFn: fetchTeamCoverage });
export const teamExpertsQuery = queryOptions({ queryKey: qk.teamExperts, queryFn: fetchTeamExperts });
export const devUsersQuery = queryOptions({ queryKey: qk.devUsers, queryFn: fetchDevUsers, retry: false });

/** Everything derived from a person's levels; refreshed together after any change. */
const TEAM_VIEWS = [qk.members, qk.dashboard, qk.progress, qk.team] as const;

/** Sets or clears a self level (or N/A). Optimistic: the card and score update before the request returns. */
export function useSetLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { areaId: string; selfLevel: Level | null; isNa: boolean }) =>
      unwrap(
        api.PUT('/api/v1/assessments/me/{areaId}', {
          params: { path: { areaId: v.areaId } },
          body: { selfLevel: v.selfLevel, isNa: v.isNa },
        }),
      ),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.results });
      const previous = qc.getQueryData<Profile>(qk.results);
      if (previous) {
        const others = previous.areas.filter((a) => a.areaId !== v.areaId);
        const current = previous.areas.find((a) => a.areaId === v.areaId);
        qc.setQueryData<Profile>(qk.results, {
          ...previous,
          // Every save, the same level included, marks the area reviewed in the open period.
          reviewedAreaIds: previous.reviewedAreaIds.includes(v.areaId)
            ? previous.reviewedAreaIds
            : [...previous.reviewedAreaIds, v.areaId],
          areas: [
            ...others,
            {
              areaId: v.areaId,
              selfLevel: v.selfLevel,
              isNa: v.isNa,
              confirmedLevel: current?.confirmedLevel ?? null,
            },
          ],
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.results, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.results });
      // A lower self level can lower what counts (effective) and drop the person from coverage.
      void qc.invalidateQueries({ queryKey: qk.team });
    },
  });
}

export function useSetFocus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (areaIds: string[]) => unwrap(api.PUT('/api/v1/focus/me', { body: { areaIds } })),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.results });
      // Focus areas are among those where the person is shown who to ask (ADR 0009).
      void qc.invalidateQueries({ queryKey: qk.teamExperts });
    },
  });
}

/** The journal changes the outside-work count: refresh it with the views that show it. */
function useJournalSettled() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.journal });
    void qc.invalidateQueries({ queryKey: qk.results });
    for (const key of TEAM_VIEWS) void qc.invalidateQueries({ queryKey: key });
  };
}

export interface JournalInput {
  areaId: string | null;
  text: string;
  doneOn: string;
}

export function useAddJournalEntry() {
  const onSuccess = useJournalSettled();
  return useMutation({
    mutationFn: (v: JournalInput) => unwrap(api.POST('/api/v1/journal/me', { body: v })),
    onSuccess,
  });
}

export function useUpdateJournalEntry() {
  const onSuccess = useJournalSettled();
  return useMutation({
    mutationFn: (v: JournalInput & { entryId: string }) => {
      const { entryId, ...body } = v;
      return unwrap(api.PATCH('/api/v1/journal/me/{entryId}', { params: { path: { entryId } }, body }));
    },
    onSuccess,
  });
}

export function useDeleteJournalEntry() {
  const onSuccess = useJournalSettled();
  return useMutation({
    mutationFn: (entryId: string) =>
      unwrap(api.DELETE('/api/v1/journal/me/{entryId}', { params: { path: { entryId } } })),
    onSuccess,
  });
}

export function useConfirmLevel(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { areaId: string; level: 3 | 4 | 5; taskNote: string }) =>
      unwrap(api.POST('/api/v1/members/{userId}/confirmations', { params: { path: { userId } }, body: v })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.member(userId) });
      for (const key of TEAM_VIEWS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUndoConfirmation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmationId: string) =>
      unwrap(
        api.DELETE('/api/v1/members/{userId}/confirmations/{confirmationId}', {
          params: { path: { userId, confirmationId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.member(userId) });
      for (const key of TEAM_VIEWS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; rank?: Rank; role?: Role; status?: 'active' | 'disabled' }) => {
      const { userId, ...body } = v;
      return unwrap(api.PATCH('/api/v1/members/{userId}', { params: { path: { userId } }, body }));
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: qk.member(v.userId) });
      for (const key of TEAM_VIEWS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; displayName: string; rank: Rank; role: Role }) =>
      unwrap(api.POST('/api/v1/members', { body: v })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members }),
  });
}

// Opening, closing or reopening a period changes almost everything (limits, read-only state): refetch all.

export function useOpenPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { startsAt: string; endsAt: string; label: string | null }) =>
      unwrap(api.POST('/api/v1/periods', { body: v })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useUpdatePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: string; startsAt: string; endsAt: string; label: string | null }) => {
      const { periodId, ...body } = v;
      return unwrap(api.PATCH('/api/v1/periods/{periodId}', { params: { path: { periodId } }, body }));
    },
    onSuccess: () => qc.invalidateQueries(),
  });
}

/** Closes the open period; with `openNext` the next one opens in the same step. */
export function useClosePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      periodId: string;
      openNext?: { startsAt: string; endsAt: string; label: string | null };
    }) =>
      unwrap(
        api.POST('/api/v1/periods/{periodId}/close', {
          params: { path: { periodId: v.periodId } },
          body: v.openNext ? { openNext: v.openNext } : {},
        }),
      ),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/** "What's new" was seen or skipped in this period; home goes to the results again. */
export function useMarkWhatsNewSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) =>
      unwrap(api.POST('/api/v1/results/me/whats-new/seen', { body: { periodId } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.whatsNew }),
  });
}

export function useReopenPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: string; endsAt: string }) =>
      unwrap(
        api.POST('/api/v1/periods/{periodId}/reopen', {
          params: { path: { periodId: v.periodId } },
          body: { endsAt: v.endsAt },
        }),
      ),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useSetLocale() {
  return useMutation({
    mutationFn: (locale: Lang) => unwrap(api.PUT('/api/v1/me/locale', { body: { locale } })),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/logout')),
    onSettled: () => {
      qc.clear();
    },
  });
}

export function useDevLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => unwrap(api.POST('/api/v1/auth/dev-login', { body: { email } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

/** The bell was opened: everything until now is read. */
export function useMarkNoticesSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/me/notices/seen')),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.notices }),
  });
}

export function useUpdateTeamSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { timeZone?: string; locale?: Lang; remindDaysBeforeEnd?: number }) =>
      unwrap(api.PATCH('/api/v1/team/settings', { body: v })),
    onSuccess: (data) => {
      qc.setQueryData(qk.teamSettings, data);
    },
  });
}

/** Opt in or out of being named to teammates as someone to ask (ADR 0009). Optimistic: the box flips at once. */
export function useSetHelpVisible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (visible: boolean) => unwrap(api.PUT('/api/v1/me/help-visibility', { body: { visible } })),
    onMutate: async (visible) => {
      await qc.cancelQueries({ queryKey: qk.me });
      const previous = qc.getQueryData<Me>(qk.me);
      if (previous)
        qc.setQueryData<Me>(qk.me, { ...previous, user: { ...previous.user, helpVisible: visible } });
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.me, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.team });
    },
  });
}
